// ============================================================================
// USER-ROLE PRESENTATION + LEGACY ALIASES
// ============================================================================
//
// The role MODEL lives in `@/lib/auth/roles` — `HUB_ROLE`, the employee/client
// sets, and every predicate. `docs/engineering-rules/USER_ROLES_AND_ACCESS.md`
// names that module the source of truth.
//
// This file used to be a SECOND model: its own `USER_ROLES` constant and its
// own `isEmployeeRole` / `isAdminRole` / `isClientRole`, with the same
// semantics and the same names, while its header also claimed to be the single
// source of truth. Neither module imported the other, so callers picked one
// arbitrarily — including `lib/utils/auth/auth-authorization.ts`, which backs
// every route guard and was reading its predicates from HERE while the rule
// governing it pointed at `lib/auth/roles`. A role predicate is a security
// decision; two implementations means a fix can land in one and leave the
// other answering the old way.
//
// So the constants and predicates below are now ALIASES onto `lib/auth/roles`.
// What genuinely belongs here is PRESENTATION: the short labels and blurbs
// this app's admin UI renders, which are deliberately different strings from
// the longer `HUB_ROLE_LABELS` / `HUB_ROLE_DESCRIPTIONS` used in Slack copy.
//
// Prefer importing from `@/lib/auth/roles` in new code.
// ============================================================================

import {
  HUB_ROLE,
  isAdminRole,
  isClientRole,
  isCrmRole,
  isEmployeeRole,
  isHubRoleName,
  resolveUserRole,
  type HubRoleName,
} from "@/lib/auth/roles";

// ============================================================================
// ROLE CONSTANTS (aliases — the model is HUB_ROLE)
// ============================================================================

export const USER_ROLES = {
  WTC_ADMIN: HUB_ROLE.ADMIN,
  WTC_EMPLOYEE: HUB_ROLE.EMPLOYEE,
  WTC_CRM: HUB_ROLE.CRM,
  CLIENT_USER: HUB_ROLE.CLIENT,
} as const;

export type UserRole = HubRoleName;

export {
  isAdminRole,
  isClientRole,
  isEmployeeRole,
  resolveUserRole,
  /** @deprecated Import `isCrmRole` from `@/lib/auth/roles`. */
  isCrmRole as isCRMRole,
};

/** Check if a string is a valid user role. */
export const isValidUserRole = (role: string): role is UserRole => isHubRoleName(role);

// ============================================================================
// PRESENTATION
// ============================================================================
//
// Short forms for this app's admin tables, selects and badges. `HUB_ROLE_LABELS`
// and `HUB_ROLE_DESCRIPTIONS` in `lib/auth/roles` are the LONGER forms used in
// Slack and onboarding copy ("Admin (full access)", not "Admin"). Both are in
// use and say different things on purpose — do not collapse them.

export const ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
  [USER_ROLES.WTC_ADMIN]: "Admin",
  [USER_ROLES.WTC_EMPLOYEE]: "Employee",
  [USER_ROLES.WTC_CRM]: "CRM",
  [USER_ROLES.CLIENT_USER]: "Client",
} as const;

export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  [USER_ROLES.WTC_ADMIN]:
    "Full system access - can see everything and manage all aspects of the system",
  [USER_ROLES.WTC_EMPLOYEE]:
    "Service delivery: tickets, time entries and the contacts behind them. The sibling of the sales role, not a lesser version of it.",
  // NOT "employee access plus CRM" — that is what this string used to say, and
  // it is backwards. `wtc_crm` is the SALES role: contacts, companies, deals,
  // proposals, meetings. It does NOT do tickets or time entries, which is
  // exactly what `wtc_employee` is for. The two are siblings, not a superset
  // and a subset, and an admin picking a role from this list was being told
  // otherwise.
  [USER_ROLES.WTC_CRM]:
    "Sales: contacts, companies, deals, proposals and meetings. Does not work tickets or time entries.",
  [USER_ROLES.CLIENT_USER]: "External clients with limited access to their own tickets and data",
} as const;

export interface RoleOption {
  value: UserRole;
  label: string;
  description: string;
}

export const ROLE_OPTIONS: RoleOption[] = [
  USER_ROLES.WTC_ADMIN,
  USER_ROLES.WTC_EMPLOYEE,
  USER_ROLES.WTC_CRM,
  USER_ROLES.CLIENT_USER,
].map((value) => ({
  value,
  label: ROLE_DISPLAY_NAMES[value],
  description: ROLE_DESCRIPTIONS[value],
}));

/** Get role display name. */
export function getRoleDisplayName(role: UserRole | string): string {
  return ROLE_DISPLAY_NAMES[role as UserRole] || role;
}

/** Get role description. */
export function getRoleDescription(role: UserRole | string): string {
  return ROLE_DESCRIPTIONS[role as UserRole] || "Unknown role";
}

/**
 * Badge variant for a resolved role: admin = default, other employees =
 * secondary, clients/unknown = outline.
 */
export function getRoleBadgeVariant(
  role: string | null | undefined
): "default" | "secondary" | "outline" {
  if (!role) return "outline";
  if (isAdminRole(role)) return "default";
  if (isEmployeeRole(role)) return "secondary";
  return "outline";
}
